import PermissionButton, { currentCan } from '../../permissions/PermissionButton';
import React, { useEffect, useState, useContext, useRef, useMemo } from 'react';
import { Modal, Button, Card, Table, message, Typography, Tag, Row, Col, Spin, Alert, Form, Input, InputNumber, Popconfirm, Space, Select, Drawer, Descriptions, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, SaveOutlined, PlusOutlined, UndoOutlined, PrinterOutlined, InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';

import { useFetchFactureById, useFetchDetailFacturesByFacture, useValidateFacture, useValidateFactureTree, useSaveFacture  } from '../../hooks/factureHooks';
import CancelFactureButton from './CancelFactureButton';
import FactureItemManagement from './AddItemToFactureForm';
import SingleClient from '../client/SingleClient';
import { usePaymentModes, useDeliveryModes } from '../../hooks/listsHooks';
import {useNavigate} from 'react-router-dom';
import { getAllBonliv } from '../../models/bonlivAPI';
import { getAllDevis } from '../../models/devisAPI';
// Add these imports
import ComList from '../com/ComList';
import { useFetchComById } from "../../hooks/comHooks";
import NestedFactureTable from './NestedFactureTable';
import { useUnvalidateFacture } from '../../hooks/factureHooks';
import PrintDialog from './FacturePrintDialog';
import { 
  printFacture, 
  printFactureDirect 
} from './FacturePrintUtility';

import { 
  printBonliv, 
  printBonlivDirect 
} from '../bonliv/BonlivPrintUtility';

import { 
  printBonSortie, 
  printBonSortieDirect 
} from '../bonliv/BonSortiePrintUtility';

import {
  printEnveloppe,
  printEnveloppeDirect
} from './EnveloppePrintUtility';

import {
  printReleveExterne,
  printReleveExterneDirect
} from './ReleveExternePrintUtility';

import {
  printReleveInterne,
  printReleveInterneDirect
} from './ReleveInternePrintUtility';

import TitleRefEditor from './RefEditor';
import EnhancedEditableField from '../../utils/EnhancedEditableField';  // Adjust the path as needed
import ChangesSummaryModal from '../../utils/ChangesSummaryModal';
import { motion } from 'framer-motion';
import { CloseOutlined } from '@ant-design/icons';
import axios from 'axios';
import { getClientById, getAllClients } from '../../models/clientAPI';


const { Title, Text } = Typography;
const { Option } = Select;


const formatDate = (date) => {
  if (!date) return "Non fourni";
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};




const EditableContext = React.createContext(null);

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};



const EditableField = ({ 
  value, 
  onChange, 
  options, 
  inputType = "input",
  userPermissions = { isManager: false },
  factureStatus = { VALIDER: false, IsCanceled: false }
}) => {
  const canEdit = currentCan('invoices', 'update') && (userPermissions.isManager || (!factureStatus.VALIDER && !factureStatus.IsCanceled));

  const handleChange = (e) => {
    if (!canEdit) return;
    const newValue = inputType === "select" ? e : e.target.value;
    onChange(newValue);
  };

  if (inputType === "select") {
    return (
      <Select
        value={value}
        onChange={handleChange}
        style={{ width: '100%' }}
        disabled={!canEdit}
      >
        {options.map(option => (
          <Option key={option} value={option}>{option}</Option>
        ))}
      </Select>
    );
  }

  return (
    <Input
      value={value}
      onChange={handleChange}
      style={{ width: '100%' }}
      disabled={!canEdit}
    />
  );
};

const SingleFacture = ({ factureId, onCancel, onValidate, onUnvalidate, userPermissions = { isManager: false },
  onChangesSaved,
  onChangesDetected, onChangeView  }) => {
  const navigate = useNavigate();
  const { facture: initialFacture, loading: loadingFacture, error: errorFacture, refetch: refetchFacture } = useFetchFactureById(factureId);
  const { details: initialDetails, loading: loadingDetails, error: errorDetails, refetch: refetchDetails } = useFetchDetailFacturesByFacture(factureId);
  const { validate, isValidated, error: validationError } = useValidateFacture(factureId);
  const { validatetree, isValidatedtree, error: validationErrortree } = useValidateFactureTree(factureId);
  const [showClientModal, setShowClientModal] = useState(false);
  const [localFacture, setLocalFacture] = useState(null);
  const [localDetails, setLocalDetails] = useState(null);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const { handleSaveFacture, error: saveError, isSaved } = useSaveFacture();
  const [isAddItemModalVisible, setIsAddItemModalVisible] = useState(false);
  const [totals, setTotals] = useState({ totalTTC: 0, totalHT: 0 });
  const { unvalidate, error: unvalidateError, isUnvalidated } = useUnvalidateFacture();
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [refreshingClient, setRefreshingClient] = useState(false);

  const [isSummaryModalVisible, setIsSummaryModalVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
// Add these state declarations in the component
const [comDrawerOpen, setComDrawerOpen] = useState(false);
const [selectedCom, setSelectedCom] = useState('');
const [commercialId, setCommercialId] = useState(null);
const { Com: commercialData, loading: loadingCom } = useFetchComById(commercialId);
const [isAddItemDrawerVisible, setIsAddItemDrawerVisible] = useState(false);
const [isClosing, setIsClosing] = useState(false);
const { modes: paymentModes } = usePaymentModes();
const { modes: deliveryModes } = useDeliveryModes();
const [isBonlivDrawerVisible, setIsBonlivDrawerVisible] = useState(false);
const [nonTransferredDocuments, setNonTransferredDocuments] = useState([]);
const [loadingDocuments, setLoadingDocuments] = useState(false);

// Add these effects
useEffect(() => {
  if (localFacture?.CODE_COM) {
    setCommercialId(localFacture.CODE_COM);
  }
}, [localFacture?.CODE_COM]);

useEffect(() => {
  if (commercialData) {
    setSelectedCom(`${commercialData.nom} (${commercialData.code_com})`);
  }
}, [commercialData]);




// Add this handler
const handleComSelect = (com) => {
  handleFieldChange("CODE_COM", com);
  setCommercialId(com);
  setComDrawerOpen(false);
};

// Handler to fetch non-transferred bonlivs and devis for the client (comp)
const handleFetchComp = async () => {
  if (!localFacture?.CODE_CLT) {
    message.warning('Code client non disponible');
    return;
  }

  try {
    setLoadingDocuments(true);
    setIsBonlivDrawerVisible(true);
    
    // Build search params for bonlivs - use CLIENT name for better matching, fallback to COMPTE
    const bonlivSearchParams = {
      page: 1,
      pageSize: 100,
    };

    // Use CLIENT name if available, otherwise use COMPTE
    if (localFacture.CLIENT) {
      bonlivSearchParams.clientSearch = localFacture.CLIENT;
    } else if (localFacture.COMPTE) {
      bonlivSearchParams.compteSearch = localFacture.COMPTE;
    }

    // Build search params for devis
    const devisSearchParams = {
      page: 1,
      pageSize: 100,
    };

    if (localFacture.CLIENT) {
      devisSearchParams.clientSearch = localFacture.CLIENT;
    } else if (localFacture.COMPTE) {
      devisSearchParams.compteSearch = localFacture.COMPTE;
    }

    // Fetch both bonlivs and devis in parallel
    const [bonlivResponse, devisResponse] = await Promise.all([
      getAllBonliv(bonlivSearchParams),
      getAllDevis(devisSearchParams)
    ]);

    // Filter bonlivs that are not TRANSFERE and match the client code
    // Also exclude GRATUIT, ZERO, and GROUPE statuses
    const filteredBonlivs = bonlivResponse.data.bonlivs?.filter(bl => 
      bl.status !== 'TRANSFERE' && 
      bl.status !== 'GRATUIT' && 
      bl.status !== 'ZERO' && 
      bl.status !== 'GROUPE' && 
      bl.CODE_CLT === localFacture.CODE_CLT
    ) || [];

    // Filter devis that are not TRANSFERER and match the client code
    // Also exclude GRATUIT, ZERO, and GROUPE statuses
    const filteredDevis = devisResponse.devis?.filter(devis => 
      !devis.TRANSFERER && 
      devis.status !== 'GRATUIT' && 
      devis.status !== 'ZERO' && 
      devis.status !== 'GROUPE' && 
      devis.CODE_CLT === localFacture.CODE_CLT
    ) || [];

    // Combine and format documents with type indicator
    const combinedDocuments = [
      ...filteredBonlivs.map(bl => ({
        ...bl,
        documentType: 'BL',
        reference: bl.REF_BL,
        date: bl.DATE_BL,
        refDev: bl.REF_DEV,
        status: bl.status,
        amount: bl.MNT_TTC,
      })),
      ...filteredDevis.map(devis => ({
        ...devis,
        documentType: 'Devis',
        reference: devis.REF_DEV,
        date: devis.DATE_DEV,
        refDev: devis.REF_DEV,
        status: devis.VALIDER ? 'Validé' : 'Enregistré',
        amount: devis.MNT_TTC,
      }))
    ];

    // Sort by date (most recent first)
    combinedDocuments.sort((a, b) => new Date(b.date) - new Date(a.date));

    setNonTransferredDocuments(combinedDocuments);
    
    const totalCount = combinedDocuments.length;
    if (totalCount === 0) {
      message.info('Aucun document non transféré trouvé pour ce client');
    } else {
      const blCount = filteredBonlivs.length;
      const devisCount = filteredDevis.length;
      message.success(`${totalCount} document(s) non transféré(s) trouvé(s): ${blCount} BL, ${devisCount} Devis`);
    }
  } catch (error) {
    console.error('Error fetching non-transferred documents:', error);
    message.error(`Erreur lors de la récupération des documents: ${error.message}`);
  } finally {
    setLoadingDocuments(false);
  }
};
  const refetchAll = async () => {
    await refetchFacture();
    await refetchDetails();
  };

  useEffect(() => {
    if (initialFacture) {
      setLocalFacture({ ...initialFacture });
    }
  }, [initialFacture]);

  useEffect(() => {
    if (initialDetails) {
      setLocalDetails([...initialDetails]);
    }
  }, [initialDetails]);

  // Calculate totals using useMemo to avoid recalculation on every render
  const calculatedTotals = useMemo(() => {
    if (!localDetails || localDetails.length === 0) return null;
    return calculateTotals(localDetails);
  }, [localDetails]);

  // Update facture and details when calculated totals change
  useEffect(() => {
    if (!calculatedTotals) return;

    const { totalTTC, totalHT, updatedItems } = calculatedTotals;

    // Update totals state
    setTotals({ totalTTC, totalHT });

    // Update facture totals
    setLocalFacture(prevFacture => ({
      ...prevFacture,
      MNT_TTC: totalTTC,
      MNT_HT: totalHT
    }));

    // Only update details if TotalHT/TotalTTC have actually changed
    const hasDetailsChanged = !localDetails.every((detail, index) => {
      const updatedDetail = updatedItems[index];
      return updatedDetail &&
             detail.TotalHT === updatedDetail.TotalHT &&
             detail.TotalTTC === updatedDetail.TotalTTC;
    });

    if (hasDetailsChanged) {
      setLocalDetails(updatedItems);
    }
  }, [calculatedTotals]);



  const handleValidateClick = async () => {
    try {
      await validate(factureId);
      await refetchAll(); // Call the new onValidate handler
      message.success('La facture a été validée avec succès.');
    } catch (error) {
      message.error(`Échec de la validation de la facture: ${error}`);
    }
  };

  const handleValidateTreeClick = async () => {
    try {
      await validatetree(factureId);
      await refetchAll(); // Call the new onValidate handler
      message.success('La facture a été validée avec succès.');
    } catch (error) {
      message.error(`Échec de la validation de la facture: ${error}`);
    }
  };




  function calculateTotals(items) {
    let totalTTC = 0;
    let totalHT = 0;

    const updatedItems = items.map((item) => {
      // Calculate TTC independently from PV_TTC
      const itemTotalTTC = calculateTotalTTC(item.QTE, item.PV_TTC, item.REMISE);

      // Calculate HT independently from PV_HT (not derived from TTC)
      const discountedPriceHT = item.PV_HT * (1 - item.REMISE / 100);
      const itemTotalHT = Math.round((item.QTE * discountedPriceHT) * 100) / 100;

      totalTTC += itemTotalTTC;
      totalHT += itemTotalHT;

      return {
        ...item,
        TotalHT: itemTotalHT,
        TotalTTC: itemTotalTTC
      };
    });

    // Round final totals to 2 decimal places
    totalTTC = Math.round(totalTTC * 100) / 100;
    totalHT = Math.round(totalHT * 100) / 100;

    return {
      updatedItems,
      totalTTC,
      totalHT
    };
  }

  function calculateTotalTTC(qte, pvTTC, remise) {
    const discountedPrice = (pvTTC * (1 - remise / 100));
    const total = qte * discountedPrice;
    // Round to 2 decimal places like Excel's ROUND function
    return Math.round(total * 100) / 100;
  }


  

  const handleSave = (row) => {
    const newData = [...localDetails];
    const index = newData.findIndex(item => row.CODE_ART === item.CODE_ART);
    const item = newData[index];
    
    // If PV_TTC is changed, set REMISE to 0
    if (row.PV_TTC !== item.PV_TTC) {
      row.REMISE = 0;
    }
    
    newData.splice(index, 1, { ...item, ...row });
    setLocalDetails(newData);
    setHasLocalChanges(true);
  };

  const handleDelete = (codeArt) => {
    const newData = localDetails.filter(item => item.CODE_ART !== codeArt);
    setLocalDetails(newData);
    setHasLocalChanges(true);
  };

  const handleFieldChange = (field, value) => {
    setLocalFacture(prevFacture => ({
      ...prevFacture,
      [field]: value
    }));
    setHasLocalChanges(true);
  };

  const handleSaveChanges = () => {
    setIsSummaryModalVisible(true);
  };
  

  useEffect(() => {
    // Notify parent component when changes occur
    if (hasLocalChanges && onChangesDetected) {
      onChangesDetected();
    }
  }, [hasLocalChanges, onChangesDetected]);

  const confirmAndSaveChanges = async () => {
    try {
      setIsSaving(true);
      // Create a copy of localFacture but remove detailFactures to avoid duplicating data
      const { detailFactures, ...factureData } = { ...localFacture };
      await handleSaveFacture(factureId, factureData, localDetails);
      await refetchAll();
      setHasLocalChanges(false); // This will trigger the effect
      if (onChangesSaved) {
        onChangesSaved(); // Still call this for immediate response
      }
      setIsSummaryModalVisible(false);
      setIsSaving(false);
      message.success('Modifications enregistrées avec succès!');
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement:', error);
      message.error(`Échec de l'enregistrement des modifications: ${error.message}`);
      setIsSaving(false);
    }
  };

  const handleAddItemSuccess = (newItem) => {
    setLocalDetails((prevDetails) => {
      const existingItemIndex = prevDetails.findIndex(item => item.CODE_ART === newItem.CODE_ART);
      
      if (existingItemIndex !== -1) {
        const updatedDetails = [...prevDetails];
        updatedDetails[existingItemIndex] = newItem;
        message.info(`Article avec CODE_ART ${newItem.CODE_ART} a été mis à jour.`);
        return updatedDetails;
      } else {
        message.success("Nouvel article ajouté avec succès.");
        return [...prevDetails, newItem];
      }
    });
    setHasLocalChanges(true);
    setIsAddItemModalVisible(false);
  };


// In SingleFacture component
const handleUnvalidateClick = async () => {
  try {
    await unvalidate(factureId);
    await refetchAll(); 
   
    message.success('La facture a été dévalidée avec succès.');
  } catch (error) {
    // Parse the error response
    let errorMessage = 'Une erreur inconnue est survenue';
    
    try {
      // For axios/fetch error responses
      if (error.response?.data) {
        errorMessage = error.response.data.message || error.response.data;
      } else if (error.message) {
        // Direct error message
        errorMessage = error.message;
      }
      
      // If it's a JSON string, parse it
      if (typeof errorMessage === 'string' && errorMessage.startsWith('{')) {
        const parsed = JSON.parse(errorMessage);
        errorMessage = parsed.message || errorMessage;
      }
    } catch (parseError) {
      console.error('Erreur lors de l\'analyse du message d\'erreur:', parseError);
    }

    Modal.error({
      title: 'Impossible de dévalider la facture',
      content: errorMessage,
      okText: 'J\'ai compris',
      width: 500,
    });
  }
};

// Update the handlePrint function in SingleFacture component
const handlePrint = async (documentType, directPrint = false) => {
  try {
    if (!localFacture || !localDetails) {
      throw new Error('Données requises pour l\'impression manquantes');
    }

    // Handle different document types with direct print option
    switch(documentType) {
      case 'bonliv':
        if (directPrint) {
          await printBonlivDirect(localFacture, localDetails, commercialData);
        } else {
          await printBonliv(localFacture, localDetails, commercialData);
        }
        break;
      case 'bonsortie':
        if (directPrint) {
          await printBonSortieDirect(localFacture, localDetails, commercialData);
        } else {
          await printBonSortie(localFacture, localDetails, commercialData);
        }
        break;
      case 'facture':
        if (directPrint) {
          await printFactureDirect(localFacture, localDetails, commercialData);
        } else {
          await printFacture(localFacture, localDetails, commercialData);
        }
        break;
      case 'enveloppe':
        try {
          // Fetch the corresponding devis data
          const API_URL = `${process.env.REACT_APP_API_BASE_URL}`;
          let devisData = null;
          
          // Check if REF_DEV is available
          if (localFacture.REF_DEV) {
            console.log(`Fetching devis data for REF_DEV: ${localFacture.REF_DEV}`);
            const response = await axios.get(`${API_URL}/fa/getdevisbyref/${localFacture.REF_DEV}`);
            devisData = response.data;
            console.log('Received devis data:', devisData);
          } else {
            console.log('No REF_DEV found in facture data');
            message.info('Aucune référence devis associée trouvée, utilisation des données de facture');
          }
          
          // Merge relevant devis data with facture data
          const printData = {
            ...localFacture,
            DATEVALID_DEVIS: devisData?.DATEVALID || null,
            HEUREVALID_DEVIS: devisData?.HEUREVALID || null,
            devisData: devisData
          };
          
          if (directPrint) {
            await printEnveloppeDirect(printData, devisData?.comercial || commercialData);
          } else {
            await printEnveloppe(printData, devisData?.comercial || commercialData);
          }
        } catch (error) {
          console.error("Erreur lors de la récupération des données du devis:", error);
          // Fall back to using facture data only
          if (directPrint) {
            await printEnveloppeDirect(localFacture, commercialData);
          } else {
            await printEnveloppe(localFacture, commercialData);
          }
        }
        break;
      case 'releve-externe':
        if (directPrint) {
          await printReleveExterneDirect(localFacture);
        } else {
          await printReleveExterne(localFacture);
        }
        break;
      case 'releve-interne':
        if (directPrint) {
          await printReleveInterneDirect(localFacture);
        } else {
          await printReleveInterne(localFacture);
        }
        break;
      default:
        throw new Error('Type de document invalide');
    }
    
    const action = directPrint ? 'envoyé à l\'imprimante' : 'préparé pour la prévisualisation';
    message.success(`Document ${action} avec succès`);
  } catch (error) {
    console.error('Erreur avec le document:', error);
    message.error(`Échec du traitement du document: ${error.message}`);
  }
}



  const getDescriptionItems = () => [
    // Row 1: Reference and Dates
    {
      key: 'clientInfo',
      label: 'Client',
      children: (
        <div>
          <Row gutter={16}>
            <Col span={14}>
              <Text strong>Client:</Text> {localFacture.CLIENT} - {localFacture.client?.VILLE}
            </Col>
            <Col span={8}>
              <Text strong>Compte:</Text> {localFacture.COMPTE || "N/A"}
              <PermissionButton permissionModule="invoices" permissionAction="update"
                onClick={async () => {
                  try {
                    if (!localFacture) return;
                    const canEdit = currentCan('invoices', 'update') && (userPermissions.isManager || (!localFacture.VALIDER && !localFacture.IsCanceled));
                    if (!canEdit) {
                      message.warning("Actualisation non autorisée pour cette facture.");
                      return;
                    }
                    setRefreshingClient(true);
                    let latestClient = null;
                    if (localFacture.client?.id) {
                      const res = await getClientById(localFacture.client.id);
                      latestClient = res.data;
                    } else if (localFacture.COMPTE) {
                      const result = await getAllClients({ compteSearch: localFacture.COMPTE, pageSize: 1 });
                      latestClient = result.clients?.[0] || null;
                    }
                    if (!latestClient) {
                      message.warning("Client introuvable pour actualisation.");
                      return;
                    }
                    setLocalFacture((prev) => ({
                      ...prev,
                      CODE_CLT: latestClient.CODE_CLT ?? prev.CODE_CLT,
                      CLIENT: latestClient.CLIENT ?? prev.CLIENT,
                      COMPTE: latestClient.COMPTE ?? prev.COMPTE,
                      MODELIV: latestClient.MODELIV ?? prev.MODELIV,
                      MODE_PAIE: latestClient.MODE_PAIE ?? prev.MODE_PAIE,
                      client: {
                        ...(prev?.client || {}),
                        ...latestClient,
                      },
                    }));
                    setHasLocalChanges(true);
                    message.success("Infos client actualisées.");
                  } catch (e) {
                    message.error(`Échec d'actualisation du client: ${e.message || e}`);
                  } finally {
                    setRefreshingClient(false);
                  }
                }}
                type="link"
                icon={<ReloadOutlined />}
                size="small"
                loading={refreshingClient}
                style={{ padding: '0 4px', marginLeft: 8 }}
              >
                Actualiser
              </PermissionButton>
            </Col>
          </Row>
          <Row style={{ marginTop: '8px' }}>
            <Col span={24}>
              <Text strong>Conditions:</Text> {localFacture.client?.conditions || "Aucune"}
            </Col>
          </Row>
        </div>
      ),
      span: 2,
    },
    {
      key: 'refBonliv',
      label: 'Références',
      children: (
        <div>
         <Text strong>Ref Devis:</Text> {localFacture.REF_DEV || "N/A"}
          
          <br />
          <Text strong>Ref BL:</Text> {localFacture.REF_BL || "N/A"}
        </div>
      ),
      span: 1,
    },
    {
      key: 'amountTTC',
      label: 'Total(ttc)',
      children: ` ${localFacture.MNT_TTC?.toFixed(2)}`,
      span: 1,
    },

    {
      key: 'amountHT',
      label: 'Total(ht)',
      children: ` ${localFacture.MNT_HT?.toFixed(2)}`,
      span: 1,
    },
  
    {
      key: 'creationDate',
      label: 'Date Facture',
      children: formatDate(localFacture.DATE_FAC),
      span: 1,
    },
    {
      key: 'paymentMode',
      label: 'M. paiem.',
      children: (
        <EditableField
          value={localFacture.MODE_PAIE}
          onChange={(value) => handleFieldChange("MODE_PAIE", value)}
          options={paymentModes}
          inputType="select"
      userPermissions={userPermissions}
      factureStatus={{
        VALIDER: localFacture.VALIDER,
        IsCanceled: localFacture.IsCanceled
      }}
    />
      ),
      span: 1,
    },
    {
      key: 'commercialCode',
      label: 'Commercial',
      children: (
        <Space>
          {loadingCom ? (
            <Spin size="small" />
          ) : (
            <span>
              {selectedCom || (commercialData ? 
                `${commercialData.nom} (${commercialData.code_com})` : 
                `ID: ${localFacture.CODE_COM}`)}
            </span>
          )}
          <PermissionButton permissionModule="invoices" permissionAction="update"
            onClick={() => setComDrawerOpen(true)} 
            type="link"
            icon={<EditOutlined />}
            size="small"
            style={{ padding: '4px' }}
          />
        </Space>
      ),
      span: 1,
    },

    
  
    {
      key: 'validationDate',
      label: 'Date Validation',
      children: formatDate(localFacture.DATEVALID),
      span: 1,
    },
  
    // Row 2: Client and Commercial Info
   

    
    
    // Row 3: Financial Information
   
    
  
    // Row 4: Additional Information
    
    {
      key: 'deliveryMode',
      label: 'M. livrais.',
      children: (
        <EditableField
          value={localFacture.MODELIV}
          onChange={(value) => handleFieldChange("MODELIV", value)}
          options={deliveryModes}
          inputType="select"
      userPermissions={userPermissions}
      factureStatus={{
        VALIDER: localFacture.VALIDER,
        IsCanceled: localFacture.IsCanceled
      }}
    />
      ),
      span: 1,
    },

    {
      key: 'remarks',
      label: 'Remarque',
      children: (
        <EnhancedEditableField
          value={localFacture.REMARQUE}
          onChange={(value) => handleFieldChange("REMARQUE", value)}
          isLongText={true}
          userPermissions={userPermissions}
          devisStatus={{
        VALIDER: localFacture.VALIDER,
        IsCanceled: localFacture.IsCanceled
      }}
    />
      ),
      span: 2,
    },
  ];


  const containerVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.95,
      transition: { 
        duration: 0.15,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    },
    visible: { 
      opacity: 1,
      scale: 1,
      transition: { 
        duration: 0.15,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    },
    exit: { 
      opacity: 0,
      scale: 0.97,
      transition: { 
        duration: 0.12,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    }
  };
  
  // Add this handler
  const handleClose = () => {
    
            onChangeView('list');
      
  };
  





  if (loadingFacture || loadingDetails) return <Spin tip="Chargement..." />;
  if (errorFacture || errorDetails) return <Alert message="Erreur" description={errorFacture || errorDetails} type="error" showIcon />;
  if (!localFacture) return <Alert message="Aucune facture trouvée" type="info" showIcon />;

  const titleStyle = {
    marginBottom: '4px',
    marginTop: '0px'
  };

  return (
    <div>
     <Card
  title={
    <Space align="center">
      <Text strong style={{ fontSize: '16px' }}>Facture - </Text>
      <TitleRefEditor
        refFac={localFacture.REF_FAC}
        dateFac={localFacture.DATE_FAC}
        onSave={(newRef) => handleFieldChange("REF_FAC", newRef)}
        onDateSave={(newDate) => handleFieldChange("DATE_FAC", newDate)}
        disabled={!localFacture.VALIDER || localFacture.IsCanceled}
      />
      <Tag 
        color={localFacture.IsCanceled ? 'red' : (localFacture.VALIDER ? 'green' : 'orange')} 
      >
        {localFacture.IsCanceled ? 'Annulée' : (localFacture.VALIDER ? 'Validé' : 'Enregistré')}
      </Tag>
    </Space>
  }
  bordered={false}
        extra={
          <Space>


{hasLocalChanges && (
              <PermissionButton
                icon={<UndoOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: 'Annuler toutes les modifications',
                    content: 'Êtes-vous sûr de vouloir annuler toutes les modifications? Cela rétablira toutes les modifications à l\'état sauvegardé précédemment.',
                    okText: 'Oui, tout annuler',
                    cancelText: 'Non, garder les modifications',
                    onOk: () => {
                      setLocalFacture({ ...initialFacture });
                      setLocalDetails([...initialDetails]);
                      setHasLocalChanges(false);
                      message.success('Toutes les modifications ont été annulées');
                    }
                  });
                }}
              >
                Annuler les modif.
              </PermissionButton>
            )}
            {!localFacture.VALIDER && !localFacture.IsCanceled && (
              <PermissionButton permissionModule="invoices" permissionAction="validate"
                type="primary" 
                onClick={handleValidateClick}
                disabled={hasLocalChanges}
              >
                Valider Fa.
              </PermissionButton>
            )}
            
            {localFacture.VALIDER && !localFacture.IsCanceled && (
              <Space>
              <PermissionButton permissionModule="invoices" permissionAction="print"
      onClick={() => setIsPrintDialogOpen(true)}
    icon={<PrinterOutlined />}
>
    Imprimer
    
</PermissionButton>
                {userPermissions.isManager && ( <PermissionButton permissionModule="invoices" permissionAction="unvalidate"
                  type="primary"
                  icon={<UndoOutlined />}
                  onClick={() => {
                    Modal.confirm({
                      title: 'Dévalider Facture',
                      content: 'Êtes-vous sûr de vouloir dévalider cette facture? Cela la remettra à son état avant validation.',
                      okText: 'Oui, Dévalider',
                      cancelText: 'Non, Annuler',
                      type: 'warning',
                      onOk: handleUnvalidateClick
                    });
                  }}
                >
                  Réintialiser
                </PermissionButton> )}
                <CancelFactureButton refFAC={factureId} onCancel={onCancel}/>
              </Space>
            )}
            <PermissionButton permissionModule="invoices" permissionAction="update"
              type="primary"
              onClick={handleSaveChanges}
              disabled={!hasLocalChanges}
            >
              Enregistrer Modif.
            </PermissionButton>
            {!localFacture.VALIDER && !localFacture.IsCanceled && (
              <PermissionButton permissionModule="invoices" permissionAction="validate"
                type="primary" 
                onClick={handleValidateTreeClick}
                disabled={hasLocalChanges}
              >
                V3
              </PermissionButton>
            )}
 <Button 
  type="default"
  onClick={handleClose}
  style={{ 
    backgroundColor: 'white',
    color: '#EE2625',
    border: '1px solid #EE2625',
  }}
>
  SORTIR
</Button>

          </Space>
        }
      >
        <Descriptions
          bordered
          column={{
            xs: 1,
            sm: 2,
            md: 3,
          }}
          size="small"
          items={getDescriptionItems()}
          labelStyle={{ fontWeight: 'bold', textAlign: 'right' }}
          contentStyle={{ minWidth: '150px' }}
        />
      </Card>
  
      <Card 
        title={
          <Space>
            <span>Articles dans la Facture</span>
            <Tag color={localFacture?.CODE_ENT ? "blue" : "default"}>
              Entrepôt: {localFacture?.CODE_ENT 
                ? (localFacture.entrepot ? `${localFacture.entrepot.code_ent} - ${localFacture.entrepot.name}` : localFacture.CODE_ENT)
                : "Non défini"}
            </Tag>
          </Space>
        }
        style={{ marginTop: 20 }} 
        extra={
          <Space>
            <Button
              onClick={handleFetchComp}
              disabled={!localFacture?.CODE_CLT}
            >
              Comp
            </Button>
            <PermissionButton permissionModule="invoices" permissionAction="update"
              onClick={() => setIsAddItemDrawerVisible(true)}
              type="primary"
              disabled={localFacture.VALIDER || !userPermissions.isManager || localFacture.IsCanceled}
            >
              Ajouter un article
            </PermissionButton>
          </Space>
        }
      >
        {(validationError || validationErrortree) && <Alert type="error" showIcon message={validationError || validationErrortree} style={{ marginBottom: 12 }} />}
        <NestedFactureTable
  items={localDetails}
  handleSave={handleSave}
  handleDelete={handleDelete}
  isValidated={localFacture.VALIDER || localFacture.IsCanceled}
  isManager={userPermissions.isManager} 
/>
      </Card>
  
      <Drawer
        title="Ajouter un article à la Facture"
        width={900}
        onClose={() => setIsAddItemDrawerVisible(false)}
        open={isAddItemDrawerVisible}
      >
        <FactureItemManagement
          refFAC={factureId}
          codeEnt={localFacture?.CODE_ENT}
          onSuccess={(newItem) => {
            handleAddItemSuccess(newItem);
            setIsAddItemDrawerVisible(false);
          }}
        />
      </Drawer>
  
      <Drawer
        title="Sélectionner un commercial"
        width={720}
        onClose={() => setComDrawerOpen(false)}
        open={comDrawerOpen}
      >
        <ComList onSelectCom={handleComSelect} />
      </Drawer>
      <Drawer
        title="Complementaire"
        width={900}
        onClose={() => setIsBonlivDrawerVisible(false)}
        open={isBonlivDrawerVisible}
      >
        {loadingDocuments ? (
          <Spin tip="Chargement des documents..." />
        ) : (
          <div>
            {nonTransferredDocuments.length === 0 ? (
              <Alert
                message="Aucun document non transféré"
                description="Tous les bons de livraison et devis pour ce client ont été transférés."
                type="info"
                showIcon
              />
            ) : (
              <Table
                dataSource={nonTransferredDocuments}
                rowKey={(record) => `${record.documentType}-${record.reference}`}
                pagination={{ pageSize: 10 }}
                columns={[
                  {
                    title: 'Type',
                    dataIndex: 'documentType',
                    key: 'documentType',
                    width: 80,
                    render: (type) => (
                      <Tag color={type === 'BL' ? 'blue' : 'green'}>
                        {type}
                      </Tag>
                    ),
                  },
                  {
                    title: 'Référence',
                    dataIndex: 'reference',
                    key: 'reference',
                  },
                  {
                    title: 'Date',
                    dataIndex: 'date',
                    key: 'date',
                    render: (date) => formatDate(date),
                  },
                  {
                    title: 'Référence Devis',
                    dataIndex: 'refDev',
                    key: 'refDev',
                    render: (refDev, record) => {
                      // For BL, show REF_DEV, for Devis, show REF_DEV (same)
                      return refDev || 'N/A';
                    },
                  },
                  {
                    title: 'Statut',
                    dataIndex: 'status',
                    key: 'status',
                    render: (status, record) => {
                      if (record.documentType === 'BL') {
                        const colorMap = {
                          'EN_PREPARATION': 'orange',
                          'PREPARE': 'blue',
                          'VALIDE': 'green',
                          'GRATUIT': 'purple',
                          'ZERO': 'cyan',
                        };
                        return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
                      } else {
                        // For Devis
                        return <Tag color={status === 'Validé' ? 'green' : 'orange'}>{status}</Tag>;
                      }
                    },
                  },
                  {
                    title: 'Montant TTC',
                    dataIndex: 'amount',
                    key: 'amount',
                    render: (amount) => amount?.toFixed(2) || '0.00',
                  },
                  {
                    title: 'Remarque',
                    dataIndex: 'REMARQUE',
                    key: 'REMARQUE',
                    render: (remarque) => remarque || '-',
                  },
                ]}
              />
            )}
          </div>
        )}
      </Drawer>
      <PrintDialog 
    open={isPrintDialogOpen}
    onClose={() => setIsPrintDialogOpen(false)}
    onPrint={handlePrint}
    REF_FAC={localFacture.REF_FAC}
/>
<ChangesSummaryModal
  visible={isSummaryModalVisible}
  onClose={() => setIsSummaryModalVisible(false)}
  onConfirm={confirmAndSaveChanges}
  originalBonliv={initialFacture}
  modifiedBonliv={localFacture}
  originalItems={initialDetails}
  modifiedItems={localDetails}
  loading={isSaving}
/>
    </div>
  );
};

export default SingleFacture;
